import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ProjectModal({
  isOpen,
  onClose
}: ProjectModalProps) {
  const {
    language
  } = useLanguage();
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    description: ''
  });
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Prevent iOS zoom on input focus
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      // Restore normal viewport
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };
  }, [isOpen, onClose]);
  // Countdown timer for auto-close
  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showSuccess && countdown === 0) {
      handleClose();
    }
  }, [showSuccess, countdown]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    setCountdown(5);
  };
  const handleClose = () => {
    setShowSuccess(false);
    setCountdown(5);
    setFormData({
      name: '',
      email: '',
      company: '',
      projectType: '',
      budget: '',
      description: ''
    });
    onClose();
  };
  const accentColor = '#00d4ff'; // Cyan - matches primary CTA
  return <AnimatePresence data-id="element-264">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-lg overflow-hidden pt-16 sm:pt-0" onClick={handleClose} data-id="element-265">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="relative w-full h-full sm:h-auto sm:max-w-2xl bg-dark-card border border-white/10 p-4 sm:p-8 md:p-12 overflow-y-auto sm:max-h-[90vh]" style={{
        boxShadow: `0 0 60px ${accentColor}40, 0 0 100px ${accentColor}20, inset 0 0 40px ${accentColor}08`
      }} data-id="element-266">
            {/* Corner Accents - Top Right & Bottom Left ONLY with enhanced glow */}
            <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-267">
              <div className="absolute top-0 right-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-268" />
              <div className="absolute top-0 right-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-269" />
            </div>
            <div className="absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-270">
              <div className="absolute bottom-0 left-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-271" />
              <div className="absolute bottom-0 left-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-272" />
            </div>

            {/* Close Button */}
            <button onClick={handleClose} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-cyan transition-colors z-10" aria-label="Close" data-id="element-273">
              <X size={20} className="sm:w-6 sm:h-6" data-id="element-274" />
            </button>

            {!showSuccess ? <div className="h-full flex flex-col" data-id="element-275">
                <h2 className="font-cyber text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2" data-id="element-276">
                  {language === 'fr' ? 'DÉMARRER VOTRE PROJET' : 'START YOUR PROJECT'}
                </h2>
                <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-8" data-id="element-277">
                  {language === 'fr' ? 'Décrivez votre projet et nos experts vous contacteront sous 24h' : 'Describe your project and our experts will contact you within 24h'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 flex-1 flex flex-col" data-id="element-278">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-279">
                    <div data-id="element-280">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-281">
                        {language === 'fr' ? 'Nom complet' : 'Full name'} *
                      </label>
                      <input type="text" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-282" />
                    </div>
                    <div data-id="element-283">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-284">
                        Email *
                      </label>
                      <input type="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-285" />
                    </div>
                  </div>

                  <div data-id="element-286">
                    <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-287">
                      {language === 'fr' ? 'Entreprise' : 'Company'}
                    </label>
                    <input type="text" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors" style={{
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                fontSize: '16px'
              }} data-id="element-288" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6" data-id="element-289">
                    <div data-id="element-290">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-291">
                        {language === 'fr' ? 'Type de projet' : 'Project type'}{' '}
                        *
                      </label>
                      <select required value={formData.projectType} onChange={e => setFormData({
                  ...formData,
                  projectType: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-292">
                        <option value="" data-id="element-293">
                          {language === 'fr' ? 'Sélectionner' : 'Select'}
                        </option>
                        <option value="website" data-id="element-294">
                          {language === 'fr' ? 'Site Web IA' : 'AI Website'}
                        </option>
                        <option value="application" data-id="element-295">
                          {language === 'fr' ? 'Application IA' : 'AI Application'}
                        </option>
                        <option value="cloud" data-id="element-296">
                          {language === 'fr' ? 'Infrastructure Cloud' : 'Cloud Infrastructure'}
                        </option>
                        <option value="consulting" data-id="element-297">
                          {language === 'fr' ? 'Conseil IT' : 'IT Consulting'}
                        </option>
                      </select>
                    </div>
                    <div data-id="element-298">
                      <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-299">
                        Budget *
                      </label>
                      <select required value={formData.budget} onChange={e => setFormData({
                  ...formData,
                  budget: e.target.value
                })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors" style={{
                  clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                  fontSize: '16px'
                }} data-id="element-300">
                        <option value="" data-id="element-301">
                          {language === 'fr' ? 'Sélectionner' : 'Select'}
                        </option>
                        <option value="<5k" data-id="element-302">
                          &lt; {language === 'fr' ? '5k€' : 'NZ$10k'}
                        </option>
                        <option value="5-15k" data-id="element-303">
                          {language === 'fr' ? '5-15k€' : 'NZ$10-30k'}
                        </option>
                        <option value="15-50k" data-id="element-304">
                          {language === 'fr' ? '15-50k€' : 'NZ$30-100k'}
                        </option>
                        <option value=">50k" data-id="element-305">
                          &gt; {language === 'fr' ? '50k€' : 'NZ$100k'}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div className="flex-1" data-id="element-306">
                    <label className="block text-[10px] sm:text-xs font-cyber text-gray-400 mb-1 sm:mb-2 uppercase tracking-wider" data-id="element-307">
                      {language === 'fr' ? 'Description du projet' : 'Project description'}{' '}
                      *
                    </label>
                    <textarea required rows={2} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full bg-black/50 border border-white/10 px-3 py-2 sm:px-4 sm:py-3 text-white text-base focus:border-cyan focus:outline-none transition-colors resize-none" style={{
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
                fontSize: '16px'
              }} data-id="element-308" />
                  </div>

                  <button type="submit" className="w-full relative group px-6 py-3 sm:px-8 sm:py-4 font-cyber font-bold text-xs sm:text-sm tracking-widest uppercase transition-all duration-300 overflow-hidden text-cyan hover:text-black hover:bg-cyan hover:shadow-[0_0_20px_rgba(0,212,255,0.5)]" style={{
              clipPath: 'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)'
            }} data-id="element-309">
                    <div className="absolute inset-0 border border-cyan bg-cyan/10 transition-all duration-300 group-hover:border-transparent" style={{
                clipPath: 'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)'
              }} data-id="element-310" />
                    <span className="relative z-10" data-id="element-311">
                      {language === 'fr' ? 'VALIDER LE PROJET' : 'VALIDATE PROJECT'}
                    </span>
                  </button>
                </form>
              </div> : <SuccessAnimation accentColor={accentColor} title={language === 'fr' ? 'Message envoyé avec succès' : 'Message sent successfully'} subtitle={language === 'fr' ? 'Votre projet a été validé et transmis à notre équipe.' : 'Your project has been validated and sent to our team.'} countdown={countdown} language={language} data-id="element-312" />}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function SuccessAnimation({
  accentColor,
  title,
  subtitle,
  countdown,
  language
}: {
  accentColor: string;
  title: string;
  subtitle: string;
  countdown: number;
  language: string;
}) {
  return <div className="flex flex-col items-center justify-center py-8 sm:py-12 h-full" data-id="element-313">
      <motion.div className="relative w-24 h-24 sm:w-32 sm:h-32 mb-6 sm:mb-8" data-id="element-314">
        {/* Data Flow Lines */}
        <motion.div initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: [0, 1, 0]
      }} transition={{
        duration: 0.4,
        ease: 'linear'
      }} className="absolute inset-0" data-id="element-315">
          <svg className="w-full h-full" viewBox="0 0 100 100" data-id="element-316">
            <line x1="0" y1="50" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-317" />
            <line x1="100" y1="50" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-318" />
            <line x1="50" y1="0" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-319" />
            <line x1="50" y1="100" x2="50" y2="50" stroke={accentColor} strokeWidth="1" opacity="0.6" data-id="element-320" />
          </svg>
        </motion.div>

        {/* Circle */}
        <motion.svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-321">
          <motion.circle cx="50" cy="50" r="35" fill="none" stroke={accentColor} strokeWidth="2" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.4,
          delay: 0.4,
          ease: 'easeInOut'
        }} style={{
          filter: `drop-shadow(0 0 4px ${accentColor}80)`
        }} data-id="element-322" />
        </motion.svg>

        {/* Checkmark */}
        <motion.svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.8
      }} data-id="element-323">
          <motion.path d="M 30 50 L 45 65 L 70 35" fill="none" stroke={accentColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          delay: 0.8,
          ease: 'easeOut'
        }} style={{
          filter: `drop-shadow(0 0 4px ${accentColor}80)`
        }} data-id="element-324" />
        </motion.svg>

        {/* Glow Pulse */}
        <motion.div className="absolute inset-0 rounded-full" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 0.3, 0]
      }} transition={{
        duration: 0.6,
        delay: 1.1
      }} style={{
        boxShadow: `0 0 30px ${accentColor}`
      }} data-id="element-325" />
      </motion.div>

      <motion.h3 initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.2
    }} className="font-cyber text-lg sm:text-2xl font-bold text-white mb-2 sm:mb-3 text-center px-4" tabIndex={0} data-id="element-326">
        {title}
      </motion.h3>
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.3
    }} className="text-gray-400 text-center text-sm sm:text-base max-w-md px-4 mb-6" data-id="element-327">
        {subtitle}
      </motion.p>

      {/* Countdown */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.4
    }} className="flex items-center gap-2 text-xs font-cyber text-gray-500 uppercase tracking-widest" data-id="element-328">
        <span data-id="element-329">
          {language === 'fr' ? 'Fermeture automatique dans' : 'Auto-closing in'}
        </span>
        <span className="text-cyan font-bold text-base" data-id="element-330">{countdown}s</span>
      </motion.div>
    </div>;
}